/**
 * ChapterLeaveConfirm.tsx — 章节编辑离开拦截确认弹窗（books 域，ChapterEditor 专用）
 *
 * 移植自原型 design/Novel-admin/src/dialogs.jsx 849–859 的 confirm 唤起：
 * 「本章有未保存的修改」三动作确认 —— 保存草稿并离开 / 放弃修改（secondary）/ 继续编辑（cancel）。
 * 共享 ~/ui/Dialog 的 ConfirmDialog 仅两态（confirm/cancel），无法承载 secondaryLabel/onSecondary，
 * 故就近实现一个三按钮 warn 态确认壳，视觉与 ConfirmDialog 一致。经 createPortal 挂 document.body。
 */

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Icons } from '~/lib/icons';

export function ChapterLeaveConfirm({
  onSave,
  onDiscard,
  onCancel,
  canSave = true,
}: {
  onSave: () => void;
  onDiscard: () => void;
  onCancel: () => void;
  canSave?: boolean;
}) {
  // ESC = 继续编辑（取消离开）
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onCancel]);

  return createPortal(
    <div className="dialog-backdrop" onClick={onCancel}>
      <div
        className="dialog"
        style={{ width: 460, overflow: 'hidden' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Hero header with warn-tinted background */}
        <div
          style={{
            padding: '20px 22px 18px',
            background: 'linear-gradient(180deg, var(--warn-soft) 0%, transparent 100%)',
            borderBottom: '1px solid var(--border)',
            position: 'relative',
          }}
        >
          <button
            className="btn btn-ghost btn-icon"
            onClick={onCancel}
            style={{ position: 'absolute', top: 10, right: 10, width: 26, height: 26 }}
          >
            <Icons.IconX size={13} />
          </button>
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 999,
                background: 'var(--bg-elev)',
                boxShadow:
                  '0 0 0 4px oklch(0.62 0.15 75 / 0.12), 0 1px 2px oklch(0.18 0.005 80 / 0.06)',
                color: 'var(--warn)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Icons.IconAlert size={18} />
            </div>
            <div style={{ flex: 1, minWidth: 0, paddingTop: 1 }}>
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  color: 'var(--warn)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  fontWeight: 600,
                  marginBottom: 4,
                }}
              >
                确认操作
              </div>
              <div
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 600,
                  fontSize: 16,
                  color: 'var(--fg-strong)',
                  letterSpacing: '-0.01em',
                  lineHeight: 1.3,
                  paddingRight: 24,
                }}
              >
                本章有未保存的修改
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 22px 18px' }}>
          <div style={{ fontSize: 13, color: 'var(--fg-muted)', lineHeight: 1.6 }}>
            保存后会保留本次编辑；若本章已发布，将形成待重发布草稿，线上版本不受影响。
            选择「放弃修改」将丢弃本次编辑的全部内容。
          </div>
        </div>

        {/* Footer — 三动作：继续编辑 / 放弃修改 / 保存草稿并离开 */}
        <div
          style={{
            padding: '12px 18px',
            borderTop: '1px solid var(--border)',
            background: 'var(--bg-subtle)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span
            style={{ fontSize: 10.5, color: 'var(--fg-faint)', fontFamily: 'var(--font-mono)' }}
          >
            <span className="kbd">Esc</span> 继续编辑
          </span>
          <div style={{ flex: 1 }} />
          <button className="btn" onClick={onCancel}>
            继续编辑
          </button>
          <button className="btn" onClick={onDiscard}>
            放弃修改
          </button>
          <button
            className="btn btn-accent"
            onClick={onSave}
            disabled={!canSave}
            title={canSave ? undefined : '请先修正标题、正文或价格校验错误'}
          >
            <Icons.IconCheck size={11} />
            保存草稿并离开
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
